import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import useFirestore from "../../../../config/fireBaseHook";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, courseDetail, onRecordUpdated }) => {
  const { updateData, deleteData } = useFirestore("FpsCourses");
  const [formData, setFormData] = useState({
    id: "",
    Course: "",
    Subject: "",
    Credits: "",
    Active: 0,
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (courseDetail) {
      setFormData({
        id: courseDetail.id || "",
        Course: courseDetail.Course || "",
        Subject: courseDetail.Subject || "",
        Credits: courseDetail.Credits || "",
        Active: courseDetail.Active === -1 ? -1 : 0,
      });
    } else {
      setFormData({
        id: "",
        Course: "",
        Subject: "",
        Credits: "",
        Active: 0,
      });
    }
  }, [courseDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        console.error("Error: ID is missing for update.");
        setSnackbarMessage("Error: ID is missing for update.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }

      const dataWithTimestamps = {
        ...formData,
        updatedAt: new Date(),
      };
      delete dataWithTimestamps.id;

      await updateData(formData.id, dataWithTimestamps);
      setSnackbarMessage("Course updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
    } catch (error) {
      console.error("Error updating course:", error);
      setSnackbarMessage(`Error updating course: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteData(formData.id);
      setSnackbarMessage("Course deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setConfirmDeleteOpen(false);
      if (onRecordUpdated) onRecordUpdated();
      onClose();
    } catch (error) {
      console.error("Error deleting course:", error);
      setSnackbarMessage(`Error deleting course: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setConfirmDeleteOpen(false);
    }
    setLoading(false);
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Course
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Course"
                name="Course"
                value={formData.Course}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subject"
                name="Subject"
                value={formData.Subject}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Credits"
                name="Credits"
                value={formData.Credits}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === -1}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleDeleteClick}
          deleting={loading}
        />
      </Dialog>

      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        deleting={loading}
        message="Are you sure you want to delete this course? This action cannot be undone."
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
